import React, { useEffect, useMemo, useState } from "react";
import { Box, Text, UnstyledButton } from "@mantine/core";
import {
  IconArrowDown,
  IconArrowUp,
  IconChevronRight,
} from "@tabler/icons-react";
import InfoTooltip from "../InfoTooltip";
import Pill from "../Pill";
import { formatCount } from "../../../utils/metricFormat";
import {
  estimateColumnWidth,
  estimateFirstColumnWidth,
  flattenVisibleRows,
  isGrouped,
  maxTreeDepth,
  TreeTableColumn,
  TreeTableNode,
} from "./treeTableModel";
import { TreeExpansion, useTreeExpansion } from "./useTreeExpansion";
import styles from "./GroupedTreeTable.module.css";

export type TreeTableDensity = "comfortable" | "compact";

export interface TreeTableSort {
  /** A column key, or FIRST_COLUMN_SORT_KEY for the label column. */
  key: string;
  dir: "asc" | "desc";
}

/** Sort key reported by `onSort` when the first (label) header is clicked. */
export const FIRST_COLUMN_SORT_KEY = "label";

export interface GroupedTreeTableProps {
  /** Accessible name for the grid, e.g. the report name. */
  ariaLabel: string;
  /** Header of the sticky first column, e.g. "Date / Staff name". */
  firstColumnLabel: string;
  /** Override the first column's minimum width (px). */
  firstColumnMinWidth?: number;
  columns: TreeTableColumn[];
  /**
   * The tree. Memoize it (useMemo) in the caller: paging ("Show N more")
   * resets whenever the `nodes` identity changes, and a fresh array on every
   * render would snap the table back to the first page.
   */
  nodes: TreeTableNode[];
  /** Sticky footer row; cells keyed by column key. */
  totals?: { label?: React.ReactNode; cells: Record<string, React.ReactNode> };
  density?: TreeTableDensity;
  /** Rows shown before "Show N more". Defaults to 100. */
  pageSize?: number;
  /** Bound for the scroll container (any CSS length). Defaults to 70dvh. */
  maxHeight?: string;
  /** Shown instead of rows when `nodes` is empty. */
  emptyState?: React.ReactNode;
  /** Lifted expansion state from `useTreeExpansion` (to drive
   *  Collapse/Expand all from outside). Managed internally when omitted. */
  expansion?: TreeExpansion;
  /** Current sort, to render the header arrow. Sorting the nodes is the
   *  caller's job. The first column shows the arrow when `sort` is null and
   *  `defaultSortDir` is set (the "sorted by default" state). */
  sort?: TreeTableSort | null;
  defaultSortDir?: "asc" | "desc";
  /** Header click; makes headers sortable buttons. */
  onSort?: (key: string) => void;
  /** Render leaf rows of a grouped tree in secondary text. */
  leafMuted?: boolean;
}

const EMPTY_CELL = "—";

function nodeTitle(node: TreeTableNode): string {
  if (node.title) return node.title;
  return typeof node.label === "string" || typeof node.label === "number"
    ? String(node.label)
    : "row";
}

function ariaSort(
  key: string,
  sort: TreeTableSort | null | undefined,
  sortable: boolean,
): "ascending" | "descending" | "none" | undefined {
  if (!sortable) return undefined;
  if (!sort || sort.key !== key) return "none";
  return sort.dir === "asc" ? "ascending" : "descending";
}

interface HeaderCellProps {
  label: string;
  sortKey: string;
  numeric: boolean;
  first?: boolean;
  info?: React.ReactNode;
  sort?: TreeTableSort | null;
  /** Arrow direction to show when this column is the implicit default sort. */
  defaultDir?: "asc" | "desc";
  onSort?: (key: string) => void;
}

function HeaderCell({
  label,
  sortKey,
  numeric,
  first = false,
  info,
  sort,
  defaultDir,
  onSort,
}: HeaderCellProps) {
  const activeDir =
    sort && sort.key === sortKey ? sort.dir : !sort ? defaultDir : undefined;
  const Arrow = activeDir === "asc" ? IconArrowUp : IconArrowDown;
  const labelNode = (
    <>
      <Text component="span" inherit className={styles.cellText}>
        {label}
      </Text>
      {activeDir && (
        <Arrow size={13} className={styles.sortArrow} aria-hidden />
      )}
    </>
  );
  const classes = [
    styles.cell,
    styles.headerCell,
    numeric ? styles.numeric : "",
    first ? `${styles.firstCell} ${styles.headerFirst}` : "",
    activeDir ? styles.sorted : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Box
      role="columnheader"
      aria-sort={ariaSort(sortKey, sort, !!onSort)}
      className={classes}
    >
      {onSort ? (
        <UnstyledButton
          type="button"
          className={styles.sortButton}
          onClick={() => onSort(sortKey)}
          title={
            activeDir && !sort
              ? `Sorted by ${label} (default)`
              : `Sort by ${label}`
          }
        >
          {labelNode}
        </UnstyledButton>
      ) : (
        labelNode
      )}
      {info && <InfoTooltip label={info} ariaLabel={`About ${label}`} />}
    </Box>
  );
}

/**
 * A grouped, collapsible report table: nested group rows (chevron, label,
 * count pill, subtotals) over leaf rows, with sticky header / first column /
 * totals footer inside one bounded scroll container, paging over the visible
 * rows ("Show 100 more"), header info tooltips, right-aligned numeric columns
 * and comfortable / compact density.
 *
 * Purely presentational: callers pass pre-formatted cell content and sorted,
 * MEMOIZED nodes (paging resets when the `nodes` identity changes). Expansion can be lifted with `useTreeExpansion` so a page-level
 * "Collapse all / Expand all" control can drive it.
 */
export default function GroupedTreeTable({
  ariaLabel,
  firstColumnLabel,
  firstColumnMinWidth,
  columns,
  nodes,
  totals,
  density = "comfortable",
  pageSize = 100,
  maxHeight,
  emptyState = "No results for these filters",
  expansion,
  sort,
  defaultSortDir,
  onSort,
  leafMuted = false,
}: GroupedTreeTableProps) {
  // Hooks can't be conditional, so the internal state is tracked even when the
  // caller passes `expansion`; it is one pass over the groups per `nodes`.
  const internalExpansion = useTreeExpansion(nodes);
  const { collapsed, toggle } = expansion ?? internalExpansion;
  const grouped = useMemo(() => isGrouped(nodes), [nodes]);

  const [limit, setLimit] = useState(pageSize);
  useEffect(() => setLimit(pageSize), [nodes, pageSize]);

  const rows = useMemo(
    () => flattenVisibleRows(nodes, collapsed),
    [nodes, collapsed],
  );
  const shown = rows.slice(0, limit);
  const remaining = rows.length - shown.length;

  const { template, minWidth } = useMemo(() => {
    const firstWidth =
      firstColumnMinWidth ??
      estimateFirstColumnWidth(firstColumnLabel, maxTreeDepth(nodes));
    const widths = columns.map(
      (c) => c.minWidth ?? estimateColumnWidth(c.label, c.numeric !== false),
    );
    return {
      template: [`minmax(${firstWidth}px, 1.4fr)`]
        .concat(widths.map((w) => `minmax(${w}px, 0.8fr)`))
        .join(" "),
      minWidth: firstWidth + widths.reduce((sum, w) => sum + w, 0),
    };
  }, [columns, firstColumnLabel, firstColumnMinWidth, nodes]);

  const rootStyle = {
    "--tree-table-max-height": maxHeight,
  } as React.CSSProperties;
  const innerStyle = {
    "--tree-table-columns": template,
    "--tree-table-min-width": `${minWidth}px`,
  } as React.CSSProperties;

  return (
    <Box
      className={styles.root}
      data-density={density}
      style={maxHeight ? rootStyle : undefined}
      role="treegrid"
      aria-label={ariaLabel}
      aria-rowcount={rows.length + 1 + (totals ? 1 : 0)}
    >
      <Box className={styles.inner} style={innerStyle}>
        <Box role="row" className={styles.headerRow}>
          <HeaderCell
            first
            label={firstColumnLabel}
            sortKey={FIRST_COLUMN_SORT_KEY}
            numeric={false}
            sort={sort}
            defaultDir={defaultSortDir}
            onSort={onSort}
          />
          {columns.map((c) => (
            <HeaderCell
              key={c.key}
              label={c.label}
              sortKey={c.key}
              numeric={c.numeric !== false}
              info={c.info}
              sort={sort}
              onSort={onSort}
            />
          ))}
        </Box>

        {shown.map(({ node, depth, isGroup, open }) => {
          const title = nodeTitle(node);
          const rowClasses = [
            styles.row,
            isGroup ? styles.group : "",
            !isGroup && grouped && leafMuted ? styles.leafMuted : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <Box
              key={node.id}
              role="row"
              aria-level={depth + 1}
              aria-expanded={isGroup ? open : undefined}
              data-depth={depth}
              className={rowClasses}
              onClick={isGroup ? () => toggle(node.id) : undefined}
            >
              <Box
                role="rowheader"
                className={`${styles.cell} ${styles.firstCell}`}
                style={{ "--tree-depth": depth } as React.CSSProperties}
              >
                {grouped && (
                  <UnstyledButton
                    type="button"
                    className={styles.chevron}
                    data-open={open}
                    data-hidden={!isGroup}
                    tabIndex={isGroup ? 0 : -1}
                    aria-hidden={!isGroup || undefined}
                    aria-label={
                      isGroup
                        ? `${open ? "Collapse" : "Expand"} ${title}`
                        : undefined
                    }
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      if (isGroup) toggle(node.id);
                    }}
                  >
                    <IconChevronRight size={15} aria-hidden />
                  </UnstyledButton>
                )}
                <Text component="span" inherit className={styles.cellText}>
                  {node.label}
                </Text>
                {isGroup && (
                  <Pill tone="neutral">
                    {formatCount(node.count ?? node.children?.length ?? 0)}
                  </Pill>
                )}
              </Box>
              {columns.map((c) => (
                <Box
                  key={c.key}
                  role="gridcell"
                  className={`${styles.cell} ${
                    c.numeric !== false ? styles.numeric : ""
                  }`}
                >
                  <Text component="span" inherit className={styles.cellText}>
                    {node.cells[c.key] ?? EMPTY_CELL}
                  </Text>
                </Box>
              ))}
            </Box>
          );
        })}

        {/* Every child of the treegrid is a row (row > gridcell), including
            the empty state and the paging control. */}
        {nodes.length === 0 && (
          <Box role="row" className={styles.empty}>
            <Text component="span" role="gridcell" inherit>
              {emptyState}
            </Text>
          </Box>
        )}

        {remaining > 0 && (
          <Box role="row" className={styles.moreRow}>
            <Box role="gridcell">
              <UnstyledButton
                type="button"
                className={styles.moreButton}
                onClick={() => setLimit((n) => n + pageSize)}
              >
                Show {formatCount(Math.min(pageSize, remaining))} more of{" "}
                {formatCount(remaining)}
              </UnstyledButton>
            </Box>
          </Box>
        )}

        {totals && nodes.length > 0 && (
          <Box role="row" className={styles.footerRow}>
            <Box
              role="rowheader"
              className={`${styles.cell} ${styles.firstCell} ${styles.footerFirst}`}
            >
              <Text component="span" inherit className={styles.cellText}>
                {totals.label ?? "Total"}
              </Text>
            </Box>
            {columns.map((c) => (
              <Box
                key={c.key}
                role="gridcell"
                className={`${styles.cell} ${
                  c.numeric !== false ? styles.numeric : ""
                }`}
              >
                <Text component="span" inherit className={styles.cellText}>
                  {totals.cells[c.key] ?? EMPTY_CELL}
                </Text>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
